import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Check, X, ChevronRight } from "lucide-react";
import { api } from "@/lib/api";
import { useSite } from "@/context/SiteContext";
import { useSeo } from "@/hooks/useSeo";
import { safeHref, priceInfo, gstTag } from "@/lib/format";
import { breadcrumbLd, serviceLd } from "@/lib/jsonld";
import { faqJsonLd, FaqList } from "@/components/site/FaqList";
import { PortfolioCard, VideoLightbox } from "@/components/site/Portfolio";
import { Loading, Empty } from "@/components/site/Primitives";

const Block = ({ title, children, testid }) => (
  <div className="border-t border-[#27272A] pt-6" data-testid={testid}><h2 className="eb-label mb-3">{title}</h2>{children}</div>
);
const List = ({ items, icon: Icon, tone }) => <ul className="space-y-2">{items.map((x) => <li key={x} className={`flex gap-2 text-sm ${tone}`}><Icon size={16} className="mt-0.5 shrink-0 text-[#0055FF]" />{x}</li>)}</ul>;

const Related = ({ category }) => {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(null);
  useEffect(() => {
    if (category) api.get("/public/portfolio", { params: { category } }).then((r) => setItems(r.data.slice(0, 3))).catch(() => {});
  }, [category]);
  if (!items.length) return null;
  return <div className="mt-16"><h2 className="text-2xl font-bold mb-6">Related work</h2><div className="grid sm:grid-cols-3 gap-4">{items.map((i) => <PortfolioCard key={i.id} item={i} onOpen={setOpen} />)}</div><VideoLightbox item={open} onClose={() => setOpen(null)} /></div>;
};

export default function ServiceLanding() {
  const { slug } = useParams();
  const { site, services } = useSite();
  const [page, setPage] = useState(undefined);
  useEffect(() => {
    setPage(undefined);
    api.get(`/public/pages/${slug}`).then((r) => setPage(r.data)).catch(() => setPage(null));
  }, [slug]);
  const path = `/services/${slug}`;
  const faqs = (page?.faqs || []).map((f, i) => ({ id: `${i}`, question: f.title, answer: f.body }));
  useSeo("services", path, page ? { title: page.seo_title || `${page.title} | EditBay`, description: page.seo_description || page.summary, canonical: page.canonical, og_title: page.og_title, og_description: page.og_description, og_image: page.og_image, noindex: page.noindex } : {},
    page ? [serviceLd(site, page, path), breadcrumbLd(site, [["Home", "/"], ["Services", "/services"], [page.title, path]]), faqJsonLd(faqs)] : null);

  if (page === undefined) return <div className="eb-container py-24"><Loading rows={1} /></div>;
  if (page === null) return <div className="eb-container py-24"><Empty title="This service page isn't available."><Link to="/services" className="eb-btn-ghost">All services</Link></Empty></div>;
  const simple = services.find((s) => s.category === "Simple" && !s.custom_quote);
  return (
    <article className="eb-container py-12 md:py-16" data-testid="service-landing-page">
      <nav aria-label="Breadcrumb" className="flex items-center gap-1 text-xs text-zinc-500 mb-10">
        <Link to="/" className="hover:text-white">Home</Link><ChevronRight size={12} /><Link to="/services" className="hover:text-white">Services</Link><ChevronRight size={12} /><span className="text-zinc-300">{page.title}</span>
      </nav>
      <h1 className="text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-[-0.04em] leading-[0.95] max-w-4xl" data-testid="service-landing-title">{page.title}</h1>
      <p className="mt-6 text-lg md:text-xl text-zinc-300 max-w-3xl leading-relaxed border-l-2 border-[#0055FF] pl-5" data-testid="service-landing-summary">{page.summary}</p>
      <div className="mt-8 flex flex-wrap gap-3">
        <Link to="/enquire" className="eb-btn-primary" data-testid="service-landing-cta">Start a project</Link>
        {simple && <span className="eb-btn-ghost pointer-events-none">{priceInfo(simple).prefix} {priceInfo(simple).main} {priceInfo(simple).unit} {gstTag(site?.pricing, simple.price_inr)?.short}</span>}
      </div>
      <div className="mt-14 grid lg:grid-cols-12 gap-12">
        <div className="lg:col-span-7 space-y-8">
          {page.who_for && <Block title="Who it's for"><p className="text-zinc-300 leading-relaxed">{page.who_for}</p></Block>}
          <div className="grid sm:grid-cols-2 gap-8">
            {page.included?.length > 0 && <Block title="What's included" testid="landing-included"><List items={page.included} icon={Check} tone="text-zinc-300" /></Block>}
            {page.excluded?.length > 0 && <Block title="Excluded or scoped separately" testid="landing-excluded"><List items={page.excluded} icon={X} tone="text-zinc-500" /></Block>}
          </div>
          {page.body && <Block title="How we approach it"><p className="text-zinc-400 leading-relaxed whitespace-pre-line">{page.body}</p></Block>}
          {page.inputs?.length > 0 && <Block title="What we need from you"><List items={page.inputs} icon={Check} tone="text-zinc-300" /></Block>}
        </div>
        <aside className="lg:col-span-5 space-y-6">
          {[["Pricing", page.pricing_note, "landing-pricing"], ["First-draft turnaround", page.turnaround_note, "landing-turnaround"], ["Revisions", page.revisions_note, "landing-revisions"]].filter(([, v]) => v).map(([t, v, id]) => (
            <div key={t} className="bg-[#121212] border border-[#27272A] p-6" data-testid={id}><p className="eb-label mb-2">{t}</p><p className="text-sm text-zinc-300 leading-relaxed">{v}</p></div>
          ))}
          {page.links?.length > 0 && <div className="flex flex-wrap gap-2">{page.links.map((l) => <Link key={l.href} to={safeHref(l.href)} className="text-sm border border-[#27272A] px-3 py-2 hover:border-[#0055FF] hover:text-[#00E5FF]">{l.label}</Link>)}</div>}
        </aside>
      </div>
      <Related category={page.related_category} />
      {faqs.length > 0 && <div className="mt-16 max-w-4xl"><h2 className="text-2xl font-bold mb-6">Questions about {page.nav_label || page.title}</h2><FaqList faqs={faqs} /></div>}
    </article>
  );
}
